import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Space, Typography, Button, Tag, Table, Select, Alert, Tooltip, Input } from 'antd';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { normalizeBase, decodeJwtClaims } from '../utils';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

type CapReason = 'explicit' | 'bypass' | 'none' | undefined;

type RouteDef = {
  path: string;
  label: string;
  protected: boolean;
  feature?: string;
  action?: string;
};

const ROUTES: RouteDef[] = [
  { path: '/dashboard', label: 'Dashboard', protected: true },
  // Tenants
  { path: '/tenants', label: 'Tenants', protected: true, feature: 'TENANT_MGMT', action: 'read' },
  {
    path: '/onboarding/voicepro',
    label: 'VoicePro Onboarding',
    protected: true,
    feature: 'TENANT_MGMT',
    action: 'create',
  },
  {
    path: '/tenants/create',
    label: 'Create Tenant',
    protected: true,
    feature: 'TENANT_MGMT',
    action: 'create',
  },
  {
    path: '/tenants/:tenantId/edit',
    label: 'Edit Tenant',
    protected: true,
    feature: 'TENANT_MGMT',
    action: 'update',
  },
  {
    path: '/tenants/:tenantId/scim',
    label: 'Tenant SCIM',
    protected: true,
    feature: 'TENANT_MGMT',
    action: 'update',
  },
  {
    path: '/tenants/:tenantId/saml',
    label: 'Tenant SAML',
    protected: true,
    feature: 'TENANT_MGMT',
    action: 'update',
  },
  // User Profiles
  {
    path: '/user-profiles',
    label: 'User Profiles',
    protected: true,
    feature: 'USER_MGMT',
    action: 'read',
  },
  // Users
  { path: '/users', label: 'Users', protected: true, feature: 'USER_MGMT', action: 'read' },
  {
    path: '/users/create',
    label: 'Create User',
    protected: true,
    feature: 'USER_MGMT',
    action: 'create',
  },
  {
    path: '/users/:userId/edit',
    label: 'Edit User',
    protected: true,
    feature: 'USER_MGMT',
    action: 'update',
  },
  {
    path: '/users/:userId',
    label: 'User Detail',
    protected: true,
    feature: 'USER_MGMT',
    action: 'read',
  },
  // Roles
  { path: '/roles', label: 'Roles', protected: true, feature: 'ROLE_MGMT', action: 'read' },
  {
    path: '/roles/create',
    label: 'Create Role',
    protected: true,
    feature: 'ROLE_MGMT',
    action: 'create',
  },
  {
    path: '/roles/:roleId/edit',
    label: 'Edit Role',
    protected: true,
    feature: 'ROLE_MGMT',
    action: 'update',
  },
  {
    path: '/roles/:roleId',
    label: 'Role Detail',
    protected: true,
    feature: 'ROLE_MGMT',
    action: 'read',
  },
  // Permissions
  {
    path: '/permissions',
    label: 'Permissions',
    protected: true,
    feature: 'PERMISSION_MGMT',
    action: 'read',
  },
  {
    path: '/permissions/create',
    label: 'Create Permission',
    protected: true,
    feature: 'PERMISSION_MGMT',
    action: 'create',
  },
  {
    path: '/permissions/:permissionId/edit',
    label: 'Edit Permission',
    protected: true,
    feature: 'PERMISSION_MGMT',
    action: 'update',
  },
  {
    path: '/permissions/:permissionId',
    label: 'Permission Detail',
    protected: true,
    feature: 'PERMISSION_MGMT',
    action: 'read',
  },
  // Permission Assignments
  {
    path: '/permission-assignments',
    label: 'Permission Assignments',
    protected: true,
    feature: 'PERMISSION_ASSIGNMENT',
    action: 'read',
  },
  {
    path: '/permission-assignments/create',
    label: 'Create Assignment',
    protected: true,
    feature: 'PERMISSION_ASSIGNMENT',
    action: 'ASSIGN',
  },
  {
    path: '/permission-assignments/:assignmentId/edit',
    label: 'Edit Assignment',
    protected: true,
    feature: 'PERMISSION_ASSIGNMENT',
    action: 'UPDATE_ASSIGNMENT',
  },
  {
    path: '/permission-assignments/:assignmentId',
    label: 'Assignment Detail',
    protected: true,
    feature: 'PERMISSION_ASSIGNMENT',
    action: 'read',
  },
  // Features
  {
    path: '/features',
    label: 'Features',
    protected: true,
    feature: 'FEATURE_MGMT',
    action: 'read',
  },
  {
    path: '/features/create',
    label: 'Create Feature',
    protected: true,
    feature: 'FEATURE_MGMT',
    action: 'create',
  },
  {
    path: '/features/:featureId/edit',
    label: 'Edit Feature',
    protected: true,
    feature: 'FEATURE_MGMT',
    action: 'update',
  },
  {
    path: '/features/:featureId',
    label: 'Feature Detail',
    protected: true,
    feature: 'FEATURE_MGMT',
    action: 'read',
  },
  // Feature Flags
  {
    path: '/feature-flags',
    label: 'Feature Flags',
    protected: true,
    feature: 'FEATURE_FLAG',
    action: 'read',
  },
  {
    path: '/feature-flags/create',
    label: 'Create Feature Flag',
    protected: true,
    feature: 'FEATURE_FLAG',
    action: 'create',
  },
  {
    path: '/feature-flags/:flagId/edit',
    label: 'Edit Feature Flag',
    protected: true,
    feature: 'FEATURE_FLAG',
    action: 'update',
  },
  {
    path: '/feature-flags/:flagId',
    label: 'Feature Flag Detail',
    protected: true,
    feature: 'FEATURE_FLAG',
    action: 'read',
  },
  // Consents
  {
    path: '/consents',
    label: 'Consents',
    protected: true,
    feature: 'CONSENT_MGMT',
    action: 'read',
  },
  {
    path: '/consents/create',
    label: 'Create Consent',
    protected: true,
    feature: 'CONSENT_MGMT',
    action: 'create',
  },
  {
    path: '/consents/:consentId/edit',
    label: 'Edit Consent',
    protected: true,
    feature: 'CONSENT_MGMT',
    action: 'update',
  },
  {
    path: '/consents/:consentId',
    label: 'Consent Detail',
    protected: true,
    feature: 'CONSENT_MGMT',
    action: 'read',
  },
  // Sessions
  {
    path: '/sessions',
    label: 'Sessions',
    protected: true,
    feature: 'SESSION_MGMT',
    action: 'read',
  },
  {
    path: '/sessions/:sessionId',
    label: 'Session Detail',
    protected: true,
    feature: 'SESSION_MGMT',
    action: 'read',
  },
  // Login Attempts
  {
    path: '/login-attempts',
    label: 'Login Attempts',
    protected: true,
    feature: 'LOGIN_ATTEMPTS',
    action: 'read',
  },
  {
    path: '/login-attempts/:attemptId',
    label: 'Login Attempt Detail',
    protected: true,
    feature: 'LOGIN_ATTEMPTS',
    action: 'read',
  },
  // Invitations
  {
    path: '/invitations',
    label: 'Invitations',
    protected: true,
    feature: 'INVITATION_MGMT',
    action: 'read',
  },
  {
    path: '/invitations/create',
    label: 'Create Invitation',
    protected: true,
    feature: 'INVITATION_MGMT',
    action: 'create',
  },
  {
    path: '/invitations/:invitationId/edit',
    label: 'Edit Invitation',
    protected: true,
    feature: 'INVITATION_MGMT',
    action: 'update',
  },
  {
    path: '/invitations/:invitationId',
    label: 'Invitation Detail',
    protected: true,
    feature: 'INVITATION_MGMT',
    action: 'read',
  },
  // Settings (no capability guard in router)
  { path: '/settings', label: 'Settings', protected: true },
];

type Row = {
  key: string;
  route: string;
  feature?: string;
  action?: string;
  protected: boolean;
  capability?: 'Allowed' | 'Allowed (bypass)' | 'Denied' | '—';
  accessible: boolean;
};

export default function RouteAccessDiagnosticsPanel({
  apiBase,
  idpBase,
}: {
  apiBase: string;
  idpBase: string;
}) {
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const token = useGlobalStore((s: GlobalState) => s.token);
  const refreshToken = useGlobalStore((s: GlobalState) => s.refreshToken);
  const jti = useGlobalStore((s: GlobalState) => s.jti);

  // Tenants (names)
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [tenantsErr, setTenantsErr] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);

  const claims = useMemo(() => (token ? decodeJwtClaims(token) : undefined), [token]);
  const defaultTenantId = useMemo(() => {
    const ids: string[] = Array.isArray(claims?.['tenantIds'])
      ? (claims?.['tenantIds'] as string[])
      : [];
    return ids[0];
  }, [claims]);

  const loadTenants = useCallback(async () => {
    if (apiBase == null || !token) return;
    try {
      const base = normalizeBase(apiBase);
      const res = await fetch(`${base}/api/tenants?skip=0&take=100`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Array<{ id: string; name: string }>;
      setTenants(Array.isArray(data) ? data : []);
    } catch (e) {
      setTenantsErr((e as Error)?.message || 'Failed to load tenants');
    }
  }, [apiBase, token]);

  useEffect(() => {
    if (isAuthenticated && apiBase != null && token) {
      setTenantId((prev) => prev ?? defaultTenantId);
      void loadTenants();
    }
  }, [isAuthenticated, apiBase, token, defaultTenantId, loadTenants]);

  // RequireAuth simulation
  type RequireAuthResult = { ok: boolean; reason?: string; expired?: boolean };
  const simulateRequireAuth = useCallback(async (): Promise<RequireAuthResult> => {
    if (!token || !refreshToken || !jti) {
      return { ok: false, reason: 'missing tokens' };
    }
    try {
      const decoded = decodeJwtClaims(token) as { exp?: number } | undefined;
      const exp = typeof decoded?.exp === 'number' ? (decoded.exp as number) : undefined;
      const now = Math.floor(Date.now() / 1000);
      if (exp && exp > now) return { ok: true };
      // Try refresh when expired
      const base = normalizeBase(idpBase);
      const res = await fetch(`${base}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken, jti }),
      });
      if (res.ok) return { ok: true };
      return { ok: false, reason: `refresh HTTP ${res.status}`, expired: true };
    } catch (e) {
      return { ok: false, reason: (e as Error)?.message || 'decode/refresh error' };
    }
  }, [token, refreshToken, jti, idpBase]);

  // Capability batch
  const [rows, setRows] = useState<Row[] | null>(null);
  const [capLoading, setCapLoading] = useState(false);
  const [capError, setCapError] = useState<string | null>(null);
  // Param values keyed by param name (e.g., userId, roleId)
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const extractParams = (path: string): string[] => {
    const out: string[] = [];
    const re = /:([A-Za-z0-9_]+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(path)) !== null) {
      out.push(m[1]);
    }
    return out;
  };

  const resolveRoute = (path: string): { ok: boolean; url: string; missing: string[] } => {
    const names = extractParams(path);
    if (names.length === 0) return { ok: true, url: path, missing: [] };
    const missing = names.filter((n) => !(paramValues[n] && paramValues[n].trim().length > 0));
    if (missing.length) return { ok: false, url: path, missing };
    let url = path;
    for (const n of names) {
      url = url.replace(`:${n}`, encodeURIComponent(paramValues[n]));
    }
    return { ok: true, url, missing: [] };
  };

  const run = useCallback(async () => {
    setCapError(null);
    setRows(null);
    if (apiBase == null) return;
    try {
      setCapLoading(true);
      const auth = await simulateRequireAuth();
      const base = normalizeBase(apiBase);

      // Deduplicate feature/actions
      const pairs = Array.from(
        new Set(
          ROUTES.filter((r) => !!r.feature && !!r.action).map((r) => `${r.feature}:${r.action}`),
        ),
      )
        .map((key) => {
          const [feature, action] = key.split(':');
          return { feature: feature ?? '', action: action ?? '' };
        })
        .slice(0);

      // Call capabilities only if token present
      const reasons: Record<string, CapReason> = {};
      const allowMap: Record<string, boolean> = {};
      if (token) {
        const res = await fetch(`${base}/api/capabilities/can`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
          },
          body: JSON.stringify({ checks: pairs }),
        });
        if (!res.ok) throw new Error(`capabilities HTTP ${res.status}`);
        const data = (await res.json()) as {
          results: Array<{ feature: string; action: string; allowed: boolean; reason?: CapReason }>;
        };
        for (const r of data.results) {
          const k = `${r.feature}:${r.action}`;
          reasons[k] = r.reason;
          allowMap[k] = r.allowed;
        }
      }

      const computed: Row[] = ROUTES.map((r) => {
        const k = r.feature && r.action ? `${r.feature}:${r.action}` : undefined;
        const allowed = k ? (allowMap[k] ?? false) : true;
        const reason = k ? reasons[k] : undefined;
        const capability: Row['capability'] = !k
          ? '—'
          : allowed
            ? reason === 'bypass'
              ? 'Allowed (bypass)'
              : 'Allowed'
            : 'Denied';
        const accessible = r.protected ? !!auth.ok && (!k || allowed) : true;
        return {
          key: r.path,
          route: r.path,
          feature: r.feature,
          action: r.action,
          protected: r.protected,
          capability,
          accessible,
        } as Row;
      });
      setRows(computed);
    } catch (e) {
      setCapError((e as Error)?.message || 'Failed to compute route access');
    } finally {
      setCapLoading(false);
    }
  }, [apiBase, token, tenantId, simulateRequireAuth]);

  const navigate = useNavigate();

  const columns: ColumnsType<Row> = [
    { title: 'Route', dataIndex: 'route', key: 'route' },
    { title: 'Feature', dataIndex: 'feature', key: 'feature' },
    { title: 'Action', dataIndex: 'action', key: 'action' },
    {
      title: 'Params',
      key: 'params',
      render: (_: unknown, r: Row) => {
        const names = extractParams(r.route);
        if (names.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Space size="small" wrap>
            {names.map((n) => (
              <Input
                key={`${r.route}:${n}`}
                size="small"
                placeholder={n}
                style={{ width: 140 }}
                value={paramValues[n] ?? ''}
                onChange={(e) => setParamValues((prev) => ({ ...prev, [n]: e.target.value }))}
              />
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Protected',
      dataIndex: 'protected',
      key: 'protected',
      render: (v: boolean) => (v ? <Tag color="blue">Yes</Tag> : <Tag> No</Tag>),
    },
    {
      title: 'Capability',
      dataIndex: 'capability',
      key: 'capability',
      render: (v: Row['capability']) => {
        if (v === 'Allowed') return <Tag color="green">Allowed</Tag>;
        if (v === 'Allowed (bypass)') return <Tag color="green">Allowed (bypass)</Tag>;
        if (v === 'Denied') return <Tag>Denied</Tag>;
        return <Tag>—</Tag>;
      },
    },
    {
      title: 'Accessible Now',
      dataIndex: 'accessible',
      key: 'accessible',
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag> No</Tag>),
    },
    {
      title: 'Open',
      key: 'open',
      render: (_: unknown, r: Row) => {
        const res = resolveRoute(r.route);
        const disabled = !r.accessible || !res.ok;
        const hint = !r.accessible
          ? 'Not accessible now (auth/capability)'
          : res.ok
            ? 'Open'
            : `Missing: ${res.missing.join(', ')}`;
        return (
          <Tooltip title={hint}>
            <span>
              <Button size="small" disabled={disabled} onClick={() => navigate(res.url)}>
                Open
              </Button>
              <Button
                size="small"
                style={{ marginLeft: 6 }}
                disabled={disabled}
                onClick={() => window.open(res.url, '_blank')}
              >
                New tab
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          Route Access Diagnostics
        </Title>
      }
    >
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Text type="secondary">
          Diagnose RequireAuth redirects and capability gating across routes.
        </Text>
        <Space wrap>
          <Tag color={isAuthenticated ? 'green' : 'red'}>
            Authenticated: {String(isAuthenticated)}
          </Tag>
          <Tag color={token ? 'green' : 'default'}>Token: {token ? 'present' : 'missing'}</Tag>
          <Tag color={refreshToken ? 'green' : 'default'}>
            Refresh: {refreshToken ? 'present' : 'missing'}
          </Tag>
          <Tag color={jti ? 'green' : 'default'}>JTI: {jti ? 'present' : 'missing'}</Tag>
        </Space>
        <Space>
          <Text type="secondary">Tenant</Text>
          <Select
            size="small"
            style={{ minWidth: 260 }}
            value={tenantId}
            placeholder="none"
            allowClear
            onChange={(v) => setTenantId(v)}
            options={
              tenants.length
                ? tenants.map((t) => ({ label: t.name, value: t.id }))
                : (Array.isArray(claims?.['tenantIds'])
                    ? (claims?.['tenantIds'] as string[])
                    : []
                  ).map((id) => ({
                    label: id,
                    value: id,
                  }))
            }
          />
          {tenantsErr ? <Alert type="warning" title={`Tenants: ${tenantsErr}`} /> : null}
          <Button type="primary" onClick={() => run()} loading={capLoading} disabled={apiBase == null}>
            Run checks
          </Button>
          <Button
            onClick={async () => {
              try {
                const payload = (rows ?? []).map(({ key, ...rest }) => rest);
                await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
              } catch {
                // ignore
              }
            }}
            disabled={!rows || rows.length === 0}
          >
            Copy JSON
          </Button>
        </Space>
        {capError && <Alert type="warning" title={capError} />}
        {rows && (
          <Table
            size="small"
            pagination={{ pageSize: 10 }}
            rowKey={(r: Row) => r.key}
            columns={columns}
            dataSource={rows}
          />
        )}
      </Space>
    </Card>
  );
}
